/**
 * ANPRPanel - Automatic Number Plate Recognition Interface
 * Records car arrivals and departures with plate detection
 */
import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// UI Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getToken } from '@/lib/authToken';

// Icons
import {
  Car, Camera, ArrowRight, ArrowLeft, Clock, CheckCircle, AlertTriangle,
  Search, Plus, RefreshCw, Loader2, ImagePlus, UserPlus, History,
  LogIn, LogOut, MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANPRPanel = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('entry');
  const [plateInput, setPlateInput] = useState('');
  const [selectedGate, setSelectedGate] = useState('main');
  const [selectedCamera, setSelectedCamera] = useState('');
  const [vehicleDetails, setVehicleDetails] = useState({ type: 'car', color: '', make: '', model: '' });
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [showQuickRegister, setShowQuickRegister] = useState(false);
  const [searchPlate, setSearchPlate] = useState('');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch ANPR stats
  const { data: stats = {} } = useQuery({
    queryKey: ['anpr-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/anpr/stats`, { headers });
      if (!res.ok) return {};
      return res.json();
    },
    refetchInterval: 10000  // Auto refresh every 10 seconds
  });

  // Fetch ANPR logs
  const { data: logs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['anpr-logs', searchPlate],
    queryFn: async () => {
      let url = `${API_URL}/api/anpr/logs?limit=50`;
      if (searchPlate) url += `&plate=${encodeURIComponent(searchPlate)}`;
      const res = await fetch(url, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch cameras
  const { data: cameras = [] } = useQuery({
    queryKey: ['cameras'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/cameras`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Record detection mutation
  const recordMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/anpr/detect`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to record');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['anpr-stats']);
      queryClient.invalidateQueries(['anpr-logs']);
      toast.success(
        data.is_registered 
          ? `${data.direction === 'entry' ? 'Entry' : 'Exit'} recorded for ${data.license_plate}`
          : `Unregistered vehicle recorded: ${data.license_plate}`
      );
      setPlateInput('');
      setCapturedPhoto(null);
      setVehicleDetails({ type: 'car', color: '', make: '', model: '' });
      
      // Offer to register if unregistered
      if (!data.is_registered) {
        setShowQuickRegister(true);
      }
    },
    onError: (error) => toast.error(error.message)
  });

  // Quick register mutation
  const quickRegisterMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/anpr/quick-register`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to register');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['anpr-stats']);
      queryClient.invalidateQueries(['anpr-logs']);
      queryClient.invalidateQueries(['vehicles']);
      toast.success('Vehicle registered successfully');
      setShowQuickRegister(false);
    },
    onError: (error) => toast.error(error.message)
  });

  // Handle photo capture
  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setCapturedPhoto(base64);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle record entry/exit
  const handleRecord = (direction) => {
    if (!plateInput.trim()) {
      toast.error('Please enter a license plate');
      return;
    }
    
    recordMutation.mutate({
      license_plate: plateInput.trim().toUpperCase(),
      camera_id: selectedCamera === 'none' ? undefined : selectedCamera || undefined,
      gate: selectedGate,
      direction,
      photo_base64: capturedPhoto,
      vehicle_type: vehicleDetails.type,
      color: vehicleDetails.color || undefined,
      make: vehicleDetails.make || undefined,
      model: vehicleDetails.model || undefined
    });
  };

  // Gate options
  const gates = [
    { id: 'main', name: 'Main Gate' },
    { id: 'north', name: 'North Gate' },
    { id: 'south', name: 'South Gate' },
    { id: 'parking', name: 'Parking Entrance' },
    { id: 'delivery', name: 'Delivery Gate' }
  ];

  return (
    <div className="space-y-6" data-testid="anpr-panel">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <LogIn className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today's Entries</p>
                <p className="text-2xl font-bold">{stats.today_entries || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <LogOut className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Today's Exits</p>
                <p className="text-2xl font-bold">{stats.today_exits || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Car className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Currently On-Site</p>
                <p className="text-2xl font-bold">{stats.currently_on_site || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Unregistered Today</p>
                <p className="text-2xl font-bold">{stats.unregistered_today || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Entry/Exit Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Record Vehicle
            </CardTitle>
            <CardDescription>Enter license plate manually or capture from camera</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="entry" className="gap-2">
                  <LogIn className="h-4 w-4" />
                  Entry
                </TabsTrigger>
                <TabsTrigger value="exit" className="gap-2">
                  <LogOut className="h-4 w-4" />
                  Exit
                </TabsTrigger>
              </TabsList>

              <div className="space-y-4">
                {/* License Plate Input */}
                <div>
                  <Label>License Plate *</Label>
                  <Input
                    value={plateInput}
                    onChange={(e) => setPlateInput(e.target.value.toUpperCase())}
                    placeholder="ABC 1234"
                    className="text-2xl font-mono tracking-wider text-center h-14"
                    data-testid="input-license-plate"
                  />
                </div>

                {/* Gate Selection */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Gate</Label>
                    <Select value={selectedGate} onValueChange={setSelectedGate}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {gates.map(gate => (
                          <SelectItem key={gate.id} value={gate.id}>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              {gate.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Camera (Optional)</Label>
                    <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select camera" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No camera</SelectItem>
                        {cameras.filter(c => c.is_active).map(cam => (
                          <SelectItem key={cam.id} value={cam.id}>{cam.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vehicle Type</Label>
                    <Select value={vehicleDetails.type} onValueChange={(v) => setVehicleDetails({...vehicleDetails, type: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="car">Car</SelectItem>
                        <SelectItem value="motorcycle">Motorcycle</SelectItem>
                        <SelectItem value="truck">Truck</SelectItem>
                        <SelectItem value="van">Van</SelectItem>
                        <SelectItem value="bus">Bus</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Color</Label>
                    <Input
                      value={vehicleDetails.color}
                      onChange={(e) => setVehicleDetails({...vehicleDetails, color: e.target.value})}
                      placeholder="e.g., Black"
                    />
                  </div>
                </div>

                {/* Photo Capture */}
                <div>
                  <Label>Photo (Optional)</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoCapture}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    className="w-full h-24 border-dashed"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {capturedPhoto ? (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-500" />
                        Photo captured
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <ImagePlus className="h-8 w-8 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Click to capture photo</span>
                      </div>
                    )}
                  </Button>
                </div>

                {/* Action Buttons */}
                <div className="pt-4">
                  <Button
                    className="w-full h-14 text-lg"
                    variant={activeTab === 'entry' ? 'default' : 'destructive'}
                    onClick={() => handleRecord(activeTab)}
                    disabled={recordMutation.isPending || !plateInput.trim()}
                    data-testid={`record-${activeTab}-btn`}
                  >
                    {recordMutation.isPending ? (
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    ) : activeTab === 'entry' ? (
                      <LogIn className="h-5 w-5 mr-2" />
                    ) : (
                      <LogOut className="h-5 w-5 mr-2" />
                    )}
                    Record {activeTab === 'entry' ? 'Entry' : 'Exit'}
                  </Button>
                </div>
              </div>
            </Tabs>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Recent Activity
                </CardTitle>
                <CardDescription>Last 50 detections</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search plate..."
                    value={searchPlate}
                    onChange={(e) => setSearchPlate(e.target.value)}
                    className="pl-9 w-40"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => queryClient.invalidateQueries(['anpr-logs'])}
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {logsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Car className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No detections recorded yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50"
                      data-testid={`anpr-log-${log.id}`}
                    >
                      <div className={`p-2 rounded-lg ${log.direction === 'entry' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {log.direction === 'entry' ? (
                          <LogIn className={`h-4 w-4 text-green-600`} />
                        ) : (
                          <LogOut className={`h-4 w-4 text-red-600`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold">{log.license_plate}</span>
                          {log.is_registered ? (
                            <Badge variant="outline" className="text-green-600 border-green-300">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Registered
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Unknown
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {gates.find(g => g.id === log.gate)?.name || log.gate}
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                      {log.vehicle_info?.color && (
                        <Badge variant="secondary">{log.vehicle_info.color}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Quick Register Dialog */}
      <Dialog open={showQuickRegister} onOpenChange={setShowQuickRegister}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Register Unknown Vehicle?</DialogTitle>
            <DialogDescription>
              This vehicle is not in the system. Would you like to register it now?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-2xl font-mono font-bold">{plateInput}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Make</Label>
                <Input
                  value={vehicleDetails.make}
                  onChange={(e) => setVehicleDetails({...vehicleDetails, make: e.target.value})}
                  placeholder="e.g., Toyota"
                />
              </div>
              <div>
                <Label>Model</Label>
                <Input
                  value={vehicleDetails.model}
                  onChange={(e) => setVehicleDetails({...vehicleDetails, model: e.target.value})}
                  placeholder="e.g., Camry"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickRegister(false)}>
              Skip
            </Button>
            <Button
              onClick={() => quickRegisterMutation.mutate({
                license_plate: plateInput,
                gate: selectedGate,
                direction: activeTab,
                vehicle_type: vehicleDetails.type,
                color: vehicleDetails.color,
                make: vehicleDetails.make,
                model: vehicleDetails.model
              })}
              disabled={quickRegisterMutation.isPending}
            >
              {quickRegisterMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Register Vehicle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ANPRPanel;
